import React, { useState, useEffect } from 'react';
import { Select, Input, Button, message, Upload, Radio, Spin, Tooltip, Table, Typography, InputNumber, Tag } from 'antd';
import { FileText, Upload as UploadIcon, PlusCircle, Trash2 } from 'lucide-react';
import { InfoCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

// Display names for a professional look
const displayNameMap = {
  fund_name: "Fund Name",
  ticker_symbol: "Ticker Symbol",
  type: "Fund Type",
  peer_group: "Peer Group",
  fi360_fiduciary_score: "Fi360 Fiduciary Score",
  watch_list_status: "Watch List Status",
  reason_for_watch_list_status: "Reason for Watch List Status",
  expense_ratio: "Expense Ratio",
  total_assets_invested: "Total Assets Invested",
  percent_of_total_plan_assets: "Percent of Total Plan Assets",
  page: "Page"
};

// Options for dropdown fields
const watchListOptions = [
  "Not On Watchlist",
  "Watch", 
  "Remove",
  "Proposed",
  "Select..."
];

const peerGroupOptions = [
  "Large Blend",
  "Large Growth",
  "Large Value",
  "Mid-Cap Blend",
  "Mid-Cap Growth",
  "Mid-Cap Value",
  "Small Blend",
  "Small Growth",
  "Small Value",
  "Diversified Emerging Mkts",
  "Foreign Large Blend",
  "Foreign Large Growth",
  "Foreign Small/Mid Blend",
  "Global Large-Stock Growth",
  "Global Small/Mid Stock",
  "Equity Energy",
  "Equity Precious Metals",
  "Miscellaneous Sector",
  "Real Estate",
  "Global Allocation",
  "Moderate Allocation",
  "Moderately Aggressive Allocation",
  "Moderately Conservative Allocation",
  "Target-Date 2025",
  "Target-Date 2035",
  "Target-Date 2045",
  "Target-Date 2055",
  "Target-Date 2065+",
  "Target-Date Retirement",
  "Corporate Bond",
  "Emerging Markets Bond",
  "Global Bond",
  "High Yield Bond",
  "Intermediate Core-Plus Bond"
];

// Template for a single fund entry
const emptyFundTemplate = {
  fund_name: '',
  ticker_symbol: '',
  type: '',
  peer_group: '',
  fi360_fiduciary_score: null,
  watch_list_status: 'Select...',
  reason_for_watch_list_status: 'Not Applicable',
  expense_ratio: '',
  total_assets_invested: 0,
  percent_of_total_plan_assets: 0,
  action_recommendation: '',
  page: ''
};


const mapFormToApiFund = (formFund) => {
  return {
    "Fund Name": formFund.fund_name,
    "Ticker Symbol": formFund.ticker_symbol,
    "Type": formFund.type,
    "Peer Group": formFund.peer_group,
    "Fi360 Fiduciary Score": formFund.fi360_fiduciary_score,
    "Watch List Status": formFund.watch_list_status,
    "Reason for Watch List Status": formFund.reason_for_watch_list_status,
    "Expense Ratio": formFund.expense_ratio,
    "Total Assets Invested": formFund.total_assets_invested,
    "Percent of Total Plan Assets": formFund.percent_of_total_plan_assets * 100,
    "Page": formFund.page
  };
};

const AutomaticDataEntryForFundPerformance = ({ onDataSubmitted }) => {
  const [companyName, setCompanyName] = useState('');
  const [funds, setFunds] = useState([{ ...emptyFundTemplate }]);
  const [inputMethod, setInputMethod] = useState('manual');
  const [fileList, setFileList] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [extractedData, setExtractedData] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [customPeerGroups, setCustomPeerGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterWatchList, setFilterWatchList] = useState('All');

  // Fetch client names
  useEffect(() => {
    const fetchClientNames = async () => {
      try {
        const userId = localStorage.getItem('userid');
        const year = dayjs().year();
        const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/client_names/${year}`, {
          headers: { 'user-id': userId }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        const options = data.client_names.map(name => ({
          value: name,
          label: name
        }));
        setCompanyOptions(options);
      } catch (error) {
        console.error('Error fetching company names:', error);
        message.error('Failed to load client list');
      }
    };

    fetchClientNames();
  }, []);

  

  const handleFundChange = (index, field, value) => {
    const updatedFunds = [...funds];
    updatedFunds[index] = {
      ...updatedFunds[index],
      [field]: value
    };
    
    // Update reason field based on watch list status
    if (field === 'watch_list_status') {
      updatedFunds[index].reason_for_watch_list_status = 
        value === 'Not On Watchlist' ? 'Not Applicable' : updatedFunds[index].reason_for_watch_list_status;
    }
    
    setFunds(updatedFunds);
  };

  const addFund = () => {
    setFunds([...funds, { ...emptyFundTemplate }]);
  };

  const removeFund = (index) => {
    if (funds.length === 1) {
      // Don't remove the last fund, just clear it
      setFunds([{ ...emptyFundTemplate }]);
    } else {
      const updatedFunds = [...funds];
      updatedFunds.splice(index, 1);
      setFunds(updatedFunds);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!companyName) {
        message.error('Please select a company');
        return;
      }

      const userId = localStorage.getItem('userid');
      if (!userId) {
        message.error('User ID not found. Please log in again.');
        return;
      }

      setIsProcessing(true);
      const year = dayjs().year();

      // Convert form format to API format
      const fundsToSubmit = funds.map(fund => {
        const apiFund = mapFormToApiFund(fund);
        // Add required fields for the API
        apiFund["Company Name"] = companyName;
        apiFund["Current"] = true;
        apiFund["Uploaded On"] = dayjs().format('MM/DD/YYYY');
        return apiFund;
      });
      
      const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/fund-performance/${year}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId,
          'accept': 'application/json'
        },
        body: JSON.stringify({
          data: fundsToSubmit
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`);
      }

      const data = await response.json();
      message.success(data.message || 'Fund performance data submitted successfully');

      // Clear form after submission
      setFunds([{ ...emptyFundTemplate }]);
      setCompanyName('');
      setFileList([]);

      if (onDataSubmitted) {
        onDataSubmitted();
      }

    } catch (error) {
      console.error('Error submitting data:', error);
      message.error('Failed to save data: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const isFormValid = () => {
    if (!companyName) return false;
    
    if (inputMethod === 'manual') {
      // Check if at least one fund has a name
      return funds.some(fund => fund.fund_name.trim() !== '');
    } else {
      return fileList.length > 0;
    }
  };

  const handleFileUpload = (info) => {
    let newFileList = [...info.fileList].slice(-1); // Keep only the latest file
    setFileList(newFileList);

    const status = info.file.status;
    if (status === 'done') {
      message.success(`${info.file.name} file uploaded successfully.`);
    } else if (status === 'error') {
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  const getTooltipText = (key) => {
    const tooltips = {
      company_name: "The name of the company that the fund belongs to",
      fund_name: "The official name of the investment fund",
      ticker_symbol: "The trading symbol for the fund",
      type: "The category or classification of the fund (e.g., Mutual Fund, ETF, Index Fund)",
      peer_group: "The category that this fund belongs to",
      fi360_fiduciary_score: "A score from 0-100, with lower being better",
      watch_list_status: "Indicates if the fund is on a watch list",
      reason_for_watch_list_status: "Explanation for why the fund is on a watch list",
      expense_ratio: "The percentage of fund assets used for admin, management, and other expenses",
      total_assets_invested: "Dollar amount invested in this fund",
      percent_of_total_plan_assets: "Percentage of the total plan that this fund represents",
      action_recommendation: "Recommended action for this fund (if any)",
      page: "Page references in the source document"
    };
    return tooltips[key] || "Enter details";
  };

  const handlePDFProcess = async () => {
    try {
      if (fileList.length === 0 || !companyName) {
        message.error('Please select both a company and a PDF file');
        return;
      }

      const userId = localStorage.getItem('userid');
      if (!userId) {
        message.error('User ID not found. Please log in again.');
        return;
      }

      setIsProcessing(true);
      const formDataObj = new FormData();
      formDataObj.append('file', fileList[0].originFileObj);
      formDataObj.append('company_name', companyName);

      const year = dayjs().year();

      console.log('Making API call with user-id:', userId);
      
      const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/fund-performance/${year}/process-pdf`, {
        method: 'POST',
        headers: {
          'user-id': userId,
          // Do not set Content-Type with FormData, browser will set it with boundary
        },
        body: formDataObj
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`);
      }

      const data = await response.json();

      if (data.data && Array.isArray(data.data)) {
        // Format the extracted data to match our form structure
        const formattedFunds = data.data.map(fund => {
          console.log("Processing fund:", fund["Fund Name"], "Percent:", fund["Percent of Total Plan Assets"]);
          
          // Handle percentage conversion
          let percentValue = 0;
          if (fund["Percent of Total Plan Assets"]) {
            const percentStr = String(fund["Percent of Total Plan Assets"]);
            if (percentStr.includes('%')) {
              percentValue = parseFloat(percentStr.replace('%', '')) / 100;
            } else {
              percentValue = parseFloat(percentStr) / 100;
            }
          }
          
          const formattedFund = {
            fund_name: fund["Fund Name"] || '',
            ticker_symbol: fund["Ticker Symbol"] || '',
            type: fund["Type"] || '',
            peer_group: fund["Peer Group"] || '',
            fi360_fiduciary_score: fund["Fi360 Fiduciary Score"],
            watch_list_status: fund["Watch List Status"] || 'Not On Watchlist',
            reason_for_watch_list_status: fund["Reason for Watch List Status"] || 'Not Applicable',
            expense_ratio: fund["Expense Ratio"] || '',
            total_assets_invested: fund["Total Assets Invested"] || 0,
            percent_of_total_plan_assets: percentValue,
            page: fund["Page"] || '',
            current: true,
            uploaded_on: fund["Uploaded On"] || ''
          };
          
          // No normalization needed as we're using the correct statuses
          
          return formattedFund;
        });
        
        setExtractedData(formattedFunds);
        setShowReview(true);
      }

    } catch (error) {
      console.error('Error processing PDF:', error);
      message.error('Failed to process PDF: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveExtractedData = () => {
    try {
      if (!companyName) {
        message.error('Please select a company');
        return;
      }

      const userId = localStorage.getItem('userid');
      if (!userId) {
        message.error('User ID not found. Please log in again.');
        return;
      }

      setIsProcessing(true);
      const year = dayjs().year();

      // Convert extracted data to API format
      const fundsToSubmit = extractedData.map(fund => {
        const apiFund = mapFormToApiFund(fund);
        console.log(`Fund ${fund.fund_name} percent: ${fund.percent_of_total_plan_assets} -> API: ${apiFund["Percent of Total Plan Assets"]}`);
        // Add required fields for the API
        apiFund["Company Name"] = companyName;
        apiFund["Current"] = true;
        apiFund["Uploaded On"] = dayjs().format('MM/DD/YYYY');
        return apiFund;
      });
      
      // Use the POST endpoint directly instead of calling handleSubmit
      fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/fund-performance/${year}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId,
          'accept': 'application/json'
        },
        body: JSON.stringify({
          data: fundsToSubmit
        })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        message.success(data.message || 'Fund performance data submitted successfully');

        // Clear form and state after submission
        setFunds([{ ...emptyFundTemplate }]);
        setCompanyName('');
        setFileList([]);
        setExtractedData(null);
        setShowReview(false);

        if (onDataSubmitted) {
          onDataSubmitted();
        }
      })
      .catch(error => {
        console.error('Error submitting data:', error);
        message.error('Failed to save data: ' + error.message);
      })
      .finally(() => {
        setIsProcessing(false);
      });
    } catch (error) {
      console.error('Error preparing data for submission:', error);
      message.error('Failed to prepare data: ' + error.message);
      setIsProcessing(false);
    }
  };

  const handleAddCustomPeerGroup = (newPeerGroup) => {
    if (newPeerGroup && !peerGroupOptions.includes(newPeerGroup) && !customPeerGroups.includes(newPeerGroup)) {
      setCustomPeerGroups([...customPeerGroups, newPeerGroup]);
    }
  };

  // Combine default and custom peer groups
  const allPeerGroupOptions = [...peerGroupOptions, ...customPeerGroups];

  // Filter funds based on search term and watch list status
  const getFilteredFunds = (fundsToFilter) => {
    if (!fundsToFilter) return [];

    return fundsToFilter.filter(fund => {
      const matchesSearch = !searchTerm || 
        (fund.fund_name && fund.fund_name.toLowerCase().includes(searchTerm.toLowerCase())) || 
        (fund.ticker_symbol && fund.ticker_symbol.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesWatchList = filterWatchList === 'All' || 
        (filterWatchList === 'Watch' && fund.watch_list_status === 'Watch') ||
        (filterWatchList === 'Not On Watchlist' && fund.watch_list_status === 'Not On Watchlist');
      
      console.log('Fund:', fund.fund_name, 'Watch List Status:', fund.watch_list_status, 'Filter:', filterWatchList, 'Matches:', matchesWatchList);
      
      return matchesSearch && matchesWatchList;
    });
  };

  const renderFilters = () => {
    return (
      <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h3 className="text-lg font-medium text-gray-700 mb-3">Filter Funds</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search by Fund Name or Ticker
            </label>
            <Input 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Enter fund name or ticker..."
              allowClear
              prefix={<InfoCircleOutlined className="text-gray-400" />}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Watch List Status
            </label>
            <Select
              value={filterWatchList}
              onChange={setFilterWatchList}
              style={{ width: '100%' }}
            >
              <Option value="All">All Funds</Option>
              <Option value="Watch">Watch</Option>
              <Option value="Remove">Remove</Option>
              <Option value="Proposed">Proposed</Option>
              <Option value="Not On Watchlist">Not On Watchlist</Option>
            </Select>
          </div>
        </div>
      </div>
    );
  };

  const renderExtractedDataReview = () => {
    if (!extractedData || !Array.isArray(extractedData)) return null;
    
    // Filter extracted data
    const filteredExtractedData = getFilteredFunds(extractedData);

    const columns = [
      {
        title: 'Fund Name',
        dataIndex: 'fund_name',
        key: 'fund_name',
        width: 300,
        ellipsis: true,
        render: (text, record, index) => (
          <Input
            value={text}
            onChange={(e) => {
              const updatedData = [...filteredExtractedData];
              updatedData[index].fund_name = e.target.value;
              setExtractedData(updatedData);
            }}
          />
        )
      },
      {
        title: 'Ticker',
        dataIndex: 'ticker_symbol',
        key: 'ticker_symbol',
        width: 100,
        render: (text, record, index) => (
          <Input
            value={text}
            onChange={(e) => {
              const updatedData = [...filteredExtractedData];
              updatedData[index].ticker_symbol = e.target.value;
              setExtractedData(updatedData);
            }}
          />
        )
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 100,
        render: (text, record, index) => (
          <Input
            value={text}
            onChange={(e) => {
              const updatedData = [...filteredExtractedData];
              updatedData[index].type = e.target.value;
              setExtractedData(updatedData);
            }}
          />
        )
      },
      {
        title: 'Peer Group',
        dataIndex: 'peer_group',
        key: 'peer_group',
        width: 180,
        render: (text, record, index) => (
          <Select
            value={text}
            style={{ width: '100%' }}
            onChange={(value) => {
              const updatedData = [...filteredExtractedData];
              updatedData[index].peer_group = value;
              setExtractedData(updatedData);
            }}
            showSearch
            allowClear
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            dropdownRender={(menu) => (
              <div>
                {menu}
                <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8 }}>
                  <Input
                    style={{ flex: 'auto' }}
                    placeholder="Type to add custom peer group"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value) {
                        handleAddCustomPeerGroup(e.target.value);
                        const updatedData = [...filteredExtractedData];
                        updatedData[index].peer_group = e.target.value;
                        setExtractedData(updatedData);
                        e.target.value = '';
                        e.preventDefault();
                      }
                    }}
                  />
                  <Button
                    style={{ marginLeft: 8 }}
                    onClick={(e) => {
                      const input = e.target.previousSibling;
                      if (input && input.value) {
                        handleAddCustomPeerGroup(input.value);
                        const updatedData = [...filteredExtractedData];
                        updatedData[index].peer_group = input.value;
                        setExtractedData(updatedData);
                        input.value = '';
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            )}
          >
            {allPeerGroupOptions.map(option => (
              <Option key={option} value={option}>{option}</Option>
            ))}
          </Select>
        )
      },
      {
        title: 'Fi360 Score',
        dataIndex: 'fi360_fiduciary_score',
        key: 'fi360_fiduciary_score',
        width: 120,
        render: (text, record, index) => (
          <InputNumber
            value={text}
            min={0}
            max={100}
            onChange={(value) => {
              const updatedData = [...filteredExtractedData];
              updatedData[index].fi360_fiduciary_score = value;
              setExtractedData(updatedData);
            }}
            style={{ width: '100%' }}
          />
        )
      },
      {
        title: 'Watch List',
        dataIndex: 'watch_list_status',
        key: 'watch_list_status',
        width: 150,
        render: (text, record, index) => (
          <Select
            value={text}
            style={{ width: '100%' }}
            onChange={(value) => {
              const updatedData = [...filteredExtractedData];
              updatedData[index].watch_list_status = value;
              if (value === 'Not On Watchlist') {
                updatedData[index].reason_for_watch_list_status = 'Not Applicable';
              }
              setExtractedData(updatedData);
            }}
          >
            {watchListOptions.map(option => (
              <Option key={option} value={option}>{option}</Option>
            ))}
          </Select>
        )
      },
      {
        title: 'Percent of Plan Assets',
        dataIndex: 'percent_of_total_plan_assets',
        key: 'percent_of_total_plan_assets',
        width: 120,
        render: (text, record, index) => {
          console.log(`Rendering percent for ${record.fund_name}: ${text}`);
          // Ensure we have a valid number
          const safeValue = (text === null || text === undefined || isNaN(text)) ? 0 : Number(text);
          
          return (
            <InputNumber
              value={safeValue}
              min={0}
              max={1}
              step={0.0001}
              formatter={value => `${(Number(value) * 100).toFixed(2)}%`}
              parser={value => {
                const parsed = parseFloat(value.replace('%', '')) / 100;
                return isNaN(parsed) ? 0 : parsed;
              }}
              onChange={(value) => {
                const updatedData = [...filteredExtractedData];
                updatedData[index].percent_of_total_plan_assets = value;
                setExtractedData(updatedData);
              }}
              style={{ width: '100%' }}
            />
          );
        }
      },
      {
        title: 'Pages',
        dataIndex: 'page',
        key: 'page',
        width: 120,
        render: (text, record, index) => (
          <Input
            value={text}
            onChange={(e) => {
              const updatedData = [...filteredExtractedData];
              updatedData[index].page = e.target.value;
              setExtractedData(updatedData);
            }}
          />
        )
      }
    ];

    return (
      <div className="mt-8">
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
          <p className="text-amber-800">
            <strong>Review Extracted Data:</strong> We found {extractedData.length} funds in your PDF. 
            Review the information below and make any necessary corrections before saving.
          </p>
          <p className="text-amber-700 mt-2">
            Company: <strong>{companyName}</strong>
          </p>
        </div>
        
        {renderFilters()}
        
        <Title level={4}>Fund Data Preview</Title>
        <Table 
          dataSource={filteredExtractedData.map((fund, index) => ({ ...fund, key: index }))} 
          columns={columns} 
          pagination={{ pageSize: 10 }}
          className="mb-4"
          size="small"
          expandable={{
            expandedRowRender: (record, index) => (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Reason for Watch List Status</label>
                  <TextArea
                    value={record.reason_for_watch_list_status}
                    onChange={(e) => {
                      const updatedData = [...filteredExtractedData];
                      updatedData[index].reason_for_watch_list_status = e.target.value;
                      setExtractedData(updatedData);
                    }}
                    rows={2}
                    disabled={record.watch_list_status === 'Not On Watchlist'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Expense Ratio</label>
                  <Input
                    value={record.expense_ratio}
                    onChange={(e) => {
                      const updatedData = [...filteredExtractedData];
                      updatedData[index].expense_ratio = e.target.value;
                      setExtractedData(updatedData);
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Total Assets Invested</label>
                  <InputNumber
                    value={record.total_assets_invested}
                    onChange={(value) => {
                      const updatedData = [...filteredExtractedData];
                      updatedData[index].total_assets_invested = value;
                      setExtractedData(updatedData);
                    }}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            )
          }}
        />
        
        <div className="flex justify-end space-x-4 mt-4">
          <Button onClick={() => {
            setShowReview(false);
            setExtractedData(null);
            setFileList([]);
          }}>
            Cancel
          </Button>
          <Button 
            type="primary"
            onClick={handleSaveExtractedData}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Save All Funds
          </Button>
        </div>
      </div>
    );
  };

  const renderManualForm = () => {
    // Filter funds for display
    const filteredFunds = getFilteredFunds(funds);
    
    return (
      <>
        <div className="mb-4 flex justify-between items-center">
          <Title level={4}>Fund Details</Title>
          <Button 
            type="primary" 
            icon={<PlusCircle size={16} />}
            onClick={addFund}
            className="bg-green-600 hover:bg-green-700"
          >
            Add Fund
          </Button>
        </div>
        
        {filteredFunds.length === 0 ? (
          <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-500">No funds match your filter criteria.</p>
            {funds.length > 0 && (
              <Button 
                onClick={() => {setSearchTerm(''); setFilterWatchList('All');}}
                className="mt-2"
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          filteredFunds.map((fund, index) => {
            // Find the actual index in the original funds array
            const originalIndex = funds.findIndex(f => f === fund);
            return (
              <div key={originalIndex} className="mb-8 pb-6 border-b border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <Title level={5} className="mb-0">Fund #{originalIndex + 1}</Title>
                  <Button 
                    danger 
                    icon={<DeleteOutlined />} 
                    onClick={() => removeFund(originalIndex)}
                    disabled={funds.length === 1 && !fund.fund_name}
                  >
                    Remove
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center">
                        <span>{displayNameMap.fund_name}</span>
                        <Tooltip title={getTooltipText('fund_name')}>
                          <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
                        </Tooltip>
                      </div>
                    </label>
                    <Input
                      value={fund.fund_name}
                      onChange={(e) => handleFundChange(originalIndex, 'fund_name', e.target.value)}
                      placeholder="Enter fund name..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center">
                        <span>{displayNameMap.ticker_symbol}</span>
                        <Tooltip title={getTooltipText('ticker_symbol')}>
                          <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
                        </Tooltip>
                      </div>
                    </label>
                    <Input
                      value={fund.ticker_symbol}
                      onChange={(e) => handleFundChange(originalIndex, 'ticker_symbol', e.target.value)}
                      placeholder="Enter ticker symbol..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center">
                        <span>{displayNameMap.type}</span>
                        <Tooltip title={getTooltipText('type')}>
                          <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
                        </Tooltip>
                      </div>
                    </label>
                    <Input
                      value={fund.type}
                      onChange={(e) => handleFundChange(originalIndex, 'type', e.target.value)}
                      placeholder="Enter fund type..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center">
                        <span>{displayNameMap.peer_group}</span>
                        <Tooltip title={getTooltipText('peer_group')}>
                          <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
                        </Tooltip>
                      </div>
                    </label>
                    <Select
                      value={fund.peer_group}
                      onChange={(value) => handleFundChange(originalIndex, 'peer_group', value)}
                      style={{ width: '100%' }}
                      placeholder="Select peer group..."
                      showSearch
                      allowClear
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      dropdownRender={(menu) => (
                        <div>
                          {menu}
                          <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8 }}>
                            <Input
                              style={{ flex: 'auto' }}
                              placeholder="Type to add custom peer group"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.target.value) {
                                  handleAddCustomPeerGroup(e.target.value);
                                  handleFundChange(originalIndex, 'peer_group', e.target.value);
                                  e.target.value = '';
                                  e.preventDefault();
                                }
                              }}
                            />
                            <Button
                              style={{ marginLeft: 8 }}
                              onClick={(e) => {
                                const input = e.target.previousSibling;
                                if (input && input.value) {
                                  handleAddCustomPeerGroup(input.value);
                                  handleFundChange(originalIndex, 'peer_group', input.value);
                                  input.value = '';
                                }
                              }}
                            >
                              Add
                            </Button>
                          </div>
                        </div>
                      )}
                    >
                      {allPeerGroupOptions.map(option => (
                        <Option key={option} value={option}>{option}</Option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center">
                        <span>{displayNameMap.fi360_fiduciary_score}</span>
                        <Tooltip title={getTooltipText('fi360_fiduciary_score')}>
                          <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
                        </Tooltip>
                      </div>
                    </label>
                    <InputNumber
                      value={fund.fi360_fiduciary_score}
                      onChange={(value) => handleFundChange(originalIndex, 'fi360_fiduciary_score', value)}
                      min={0}
                      max={100}
                      placeholder="Enter score..."
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center">
                        <span>{displayNameMap.watch_list_status}</span>
                        <Tooltip title={getTooltipText('watch_list_status')}>
                          <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
                        </Tooltip>
                      </div>
                    </label>
                    <Select
                      value={fund.watch_list_status}
                      onChange={(value) => handleFundChange(originalIndex, 'watch_list_status', value)}
                      style={{ width: '100%' }}
                    >
                      {watchListOptions.map(option => (
                        <Option key={option} value={option}>{option}</Option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center">
                        <span>{displayNameMap.reason_for_watch_list_status}</span>
                        <Tooltip title={getTooltipText('reason_for_watch_list_status')}>
                          <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
                        </Tooltip>
                      </div>
                    </label>
                    <TextArea
                      value={fund.reason_for_watch_list_status}
                      onChange={(e) => handleFundChange(originalIndex, 'reason_for_watch_list_status', e.target.value)}
                      disabled={fund.watch_list_status === 'Not On Watchlist'}
                      rows={2}
                      placeholder="Enter reason..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center">
                        <span>{displayNameMap.expense_ratio}</span>
                        <Tooltip title={getTooltipText('expense_ratio')}>
                          <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
                        </Tooltip>
                      </div>
                    </label>
                    <Input
                      value={fund.expense_ratio}
                      onChange={(e) => handleFundChange(originalIndex, 'expense_ratio', e.target.value)}
                      placeholder="Enter expense ratio..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center">
                        <span>{displayNameMap.total_assets_invested}</span>
                        <Tooltip title={getTooltipText('total_assets_invested')}>
                          <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
                        </Tooltip>
                      </div>
                    </label>
                    <InputNumber
                      value={fund.total_assets_invested}
                      onChange={(value) => handleFundChange(originalIndex, 'total_assets_invested', value)}
                      formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/\$\s?|(,*)/g, '')}
                      style={{ width: '100%' }}
                      placeholder="Enter total assets..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center">
                        <span>{displayNameMap.percent_of_total_plan_assets}</span>
                        <Tooltip title={getTooltipText('percent_of_total_plan_assets')}>
                          <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
                        </Tooltip>
                      </div>
                    </label>
                    <InputNumber
                      value={fund.percent_of_total_plan_assets}
                      onChange={(value) => handleFundChange(originalIndex, 'percent_of_total_plan_assets', value)}
                      min={0}
                      max={1}
                      step={0.0001}
                      formatter={value => `${(value * 100).toFixed(2)}%`}
                      parser={value => value.replace('%', '') / 100}
                      style={{ width: '100%' }}
                      placeholder="Enter percentage..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center">
                        <span>{displayNameMap.page}</span>
                        <Tooltip title={getTooltipText('page')}>
                          <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
                        </Tooltip>
                      </div>
                    </label>
                    <Input
                      value={fund.page}
                      onChange={(e) => handleFundChange(originalIndex, 'page', e.target.value)}
                      placeholder="Enter page references..."
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </>
    );
  };

  return (
    <div className="m-2 mx-auto bg-white rounded-xl">
      <h1 className="text-3xl font-light text-gray-800 mb-6">Fund Performance Data Entry</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-1 gap-8 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center">
              <span>Company Name</span>
              <Tooltip title="Select the company whose fund performance data you're documenting.">
                <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
              </Tooltip>
            </div>
          </label>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Select a company"
            value={companyName}
            onChange={setCompanyName}
            className="text-lg"
            filterOption={(input, option) => 
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {companyOptions.sort((a, b) => a.label.localeCompare(b.label)).map(option => (
              <Option key={option.value} value={option.value}>{option.label}</Option>
            ))}
          </Select>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center">
              <span>Input Method</span>
              <Tooltip title="Choose between manual data entry or uploading a PDF of the investment report.">
                <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
              </Tooltip>
            </div>
          </label>
          <Radio.Group 
            onChange={(e) => setInputMethod(e.target.value)} 
            value={inputMethod}
            className="flex space-x-4"
          >
            <Radio value="manual" className="text-lg">Manual Entry</Radio>
            <Radio value="pdf" className="text-lg">Upload PDF</Radio>
          </Radio.Group>
        </div>
      </div>

      {inputMethod === 'manual' && !showReview && renderManualForm()}

      {inputMethod === 'pdf' && !showReview && (
        <div className="bg-gray-50 p-6 rounded-lg border-2 border-dashed border-gray-300">
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-blue-800">
              <strong>Note:</strong> For best results, please upload a "Fund Performance Report" or "Investment Review" PDF. Processing may take up to a minute.
            </p>
          </div>
          <Tooltip title="Upload a PDF of the investment report. The system will automatically extract fund performance information.">
            <Upload.Dragger
              name="file"
              multiple={false}
              fileList={fileList}
              onChange={handleFileUpload}
              accept=".pdf"
              beforeUpload={() => false}
              className="bg-white"
            >
              <p className="ant-upload-drag-icon">
                <UploadIcon className="h-16 w-16 text-blue-500 mx-auto" />
              </p>
              <p className="ant-upload-text text-lg font-medium mt-4">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint text-sm text-gray-500">
                Support for a single PDF file. The file will be processed to extract fund performance information.
              </p>
            </Upload.Dragger>
          </Tooltip>
        </div>
      )}

      {showReview && renderExtractedDataReview()}

      {!showReview && (
        <div className="mt-8 flex justify-end">
          <Button 
            onClick={inputMethod === 'manual' ? handleSubmit : handlePDFProcess}
            disabled={!isFormValid() || isProcessing}
            size="large"
            className={`inline-flex items-center px-6 py-3 border border-transparent text-lg font-medium rounded-md shadow-sm text-white ${
              isFormValid() && !isProcessing 
                ? 'bg-blue-600 hover:bg-blue-700' 
                : 'bg-gray-300 cursor-not-allowed'
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out`}
          >
            {isProcessing ? (
              <Spin className="mr-2" />
            ) : (
              <FileText className="h-5 w-5 mr-2" />
            )}
            {inputMethod === 'manual' ? 'Submit Fund Data' : (isProcessing ? 'Processing PDF...' : 'Process PDF')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AutomaticDataEntryForFundPerformance;